import React from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { Attachment, Documentation } from 'graphql-common';
import { ErrorLike } from '@apollo/client';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Table from '@lib/components/Table/Table';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Trans from '@lib/components/Trans/Trans';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Search from '@lib/components/Search/Search';
import useSearchParam from '@lib/hooks/useSearchParam';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getDocumentationFilePermissions from '@lib/utils/documentation/getDocumentationFilePermissions';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { IconName } from '@lib/components/Modal/enums';
import { NavItem } from '@lib/components/Menu/Menu';
import { Column } from '@lib/components/Table/types';
import DocumentBulkEditModal from 'routes/Documentation/DocumentList/DocumentBulkEditModal/DocumentBulkEditModal';

interface Props {
  assetName: string;
  bulkEditIds: string[];
  onBulkEditCompleted: () => void;
  closeDeleteModal: () => void;
  closeFileViewer: () => void;
  closeUnlinkModal: () => void;
  closeVersionNotes: () => void;
  closeBulkEditModal: () => void;
  collection: Documentation[];
  deleteLoading: boolean;
  error: ErrorLike | undefined;
  fileViewerDocument: Documentation | null;
  firstLoading: boolean;
  isBulkEditModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isUnlinkModalOpen: boolean;
  isViewLocked?: boolean;
  isViewOnly: boolean;
  loading: boolean;
  onConfirmedDelete: () => void;
  onConfirmedUnlink: () => void;
  onCreateDocument: () => void;
  tableActionsForSelected: React.ReactNode[];
  tableActionsMenu: NavItem<Documentation>[];
  tableColumns: Column<Documentation>[];
  totalCount: number;
  unlinkLoading: boolean;
  useBulkSelectResult: UseBulkSelectResult<Documentation>;
  versionNotesDocument: Documentation | null;
}

export default function AssetDocumentations(props: Props) {
  const {
    assetName,
    bulkEditIds,
    onBulkEditCompleted,
    closeDeleteModal,
    closeFileViewer,
    closeUnlinkModal,
    closeVersionNotes,
    closeBulkEditModal,
    collection,
    deleteLoading,
    error,
    fileViewerDocument,
    firstLoading,
    isBulkEditModalOpen,
    isDeleteModalOpen,
    isUnlinkModalOpen,
    isViewLocked,
    isViewOnly,
    loading,
    onConfirmedDelete,
    onConfirmedUnlink,
    onCreateDocument,
    tableActionsForSelected,
    tableActionsMenu,
    tableColumns,
    totalCount,
    unlinkLoading,
    useBulkSelectResult,
    versionNotesDocument,
  } = props;
  const { t } = useTranslation();
  const [searchQuery] = useSearchParam();
  const fileViewerPermissions =
    getDocumentationFilePermissions(fileViewerDocument);

  const emptyState = (
    <ListEmptyState
      title={t('no-documents-linked-yet')}
      text={<Trans i18nKey="no-documents-linked-yet-text" />}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="create"
                buttonText={t('create-document')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                onClick={onCreateDocument}
              />,
            ]
      }
    />
  );

  if (error) {
    return (
      <ErrorPage
        status={getStatusFromError(error)}
        description={getDescriptionFromError(error)}
      />
    );
  }

  return (
    <>
      {firstLoading ? (
        <CircleLoader />
      ) : (
        <WhiteBox>
          <DashboardTitleHeader
            title={assetName}
            actions={
              isViewOnly ? (
                <ViewOnlyChip key="view" />
              ) : (
                <Button
                  key="create"
                  buttonText={t('create-document')}
                  buttonType={ButtonTypes.primaryFilled}
                  leftIcon="add"
                  onClick={onCreateDocument}
                />
              )
            }
          />
          {(!!collection?.length || searchQuery) && (
            <Search
              placeholder={t(
                isViewLocked
                  ? 'documents-search-placeholder-base'
                  : 'documents-search-placeholder-premium',
              )}
            />
          )}
          <Table
            actionsForSelected={tableActionsForSelected}
            actionsMenu={tableActionsMenu}
            columns={tableColumns}
            data={collection}
            emptyState={emptyState}
            id="asset-documentations"
            loading={loading}
            totalEntries={totalCount}
            useBulkSelectResult={useBulkSelectResult}
          />
        </WhiteBox>
      )}
      {!isViewLocked && (
        <DocumentBulkEditModal
          ids={bulkEditIds}
          isOpen={isBulkEditModalOpen}
          onClose={closeBulkEditModal}
          onCompleted={onBulkEditCompleted}
        />
      )}
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="documents-bulk-delete-text" />}
        icon={IconName.Delete}
        isOpen={isDeleteModalOpen}
        loading={deleteLoading}
        onClose={closeDeleteModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-unlink')}
        description={<Trans i18nKey="unlink-from-equipment-confirm-text" />}
        icon={IconName.Delete}
        isOpen={isUnlinkModalOpen}
        loading={unlinkLoading}
        onClose={closeUnlinkModal}
        onConfirm={onConfirmedUnlink}
        title={t('are-you-sure')}
        useProgress
      />
      <FileViewerModal
        attachment={
          _get(fileViewerDocument, [
            'currentVersion',
            'attachmentAttached',
          ]) as Attachment
        }
        attachmentName={fileViewerDocument?.name}
        documentationVersionId={fileViewerDocument?.currentVersion?.id}
        isOpen={!!fileViewerDocument}
        onClose={closeFileViewer}
        useDownloadTool={fileViewerPermissions.allowDownload}
        usePrintTool={fileViewerPermissions.allowPrint}
      />
      <DocumentVersionNotes
        isOpen={!!versionNotesDocument}
        notes={_get(versionNotesDocument, ['currentVersion', 'notes']) || ''}
        onClose={closeVersionNotes}
      />
    </>
  );
}
